import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';
import { GenericCompanySearchableFields } from 'utils/loadInfoAndBuilding';

type GetCustomersResponse = {
  customerList: TMSCustomer[];
  tmsTenant: string;
};

export async function searchCustomers(
  tmsID: number,
  key: GenericCompanySearchableFields,
  value: string
): Promise<Result<GetCustomersResponse, ApiError>> {
  const params = [
    `tmsID=${encodeURIComponent(tmsID)}`,
    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
  ];

  try {
    const response = await axios.get<GetCustomersResponse>(
      `/customers/search?${params.join('&')}`
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'searchCustomers' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to search customers' });
    }

    if (error && error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err({
      message: error.response?.data.message || 'Failed to search customers',
    });
  }
}

/**
 * Fetches a single customer by externalTMSID via GET /customers/search.
 * Used when load/suggestion only provides customer.externalTMSID so we can
 * resolve full customer and inject into the dropdown list.
 *
 * When `externalTMSID` is missing, returns `ok(null)` without
 * calling the API to align with empty search result.
 */
export async function fetchCustomerByExternalTMSID(
  tmsID: number,
  externalTMSID: string
): Promise<Result<Maybe<TMSCustomer>, ApiError>> {
  const trimmedExternalTMSID = externalTMSID?.trim();
  if (!trimmedExternalTMSID) {
    return ok(null);
  }
  const res = await searchCustomers(
    tmsID,
    'externalTMSID',
    trimmedExternalTMSID
  );
  if (res.isErr()) {
    return err(res.error);
  }
  const firstCustomer = res.value.customerList?.[0] ?? null;
  return ok(firstCustomer);
}
